import http from 'api/httpClient';
import type { PnlLevel } from 'pages/pnl/api';

export type PendingPodLevel = PnlLevel;
export type PendingPodStatus = 'not_due' | 'overdue';

export type UpdatePendingPodDropdownsRequest = {
  selection: Record<string, string[]>;
};

export type UpdatePendingPodDropdownsResponse = {
  levels: PendingPodLevel[];
};

export async function updatePendingPodDropdowns(
  payload: UpdatePendingPodDropdownsRequest
): Promise<UpdatePendingPodDropdownsResponse> {
  return http.post('/api/v1/pending-pod-reports/update-dropdowns', { ...payload });
}

export type PendingPodLookupRequest = {
  filters: Record<string, string[]>;
  cust_code?: string;
  vendor_code?: string;
};

export type PendingPodLookupOption = {
  code: string;
  name: string;
};

export type PendingPodLookupResponse = {
  items: PendingPodLookupOption[];
};

export async function fetchPendingPodCustomers(
  payload: PendingPodLookupRequest
): Promise<PendingPodLookupResponse> {
  return http.post('/api/v1/pending-pod-reports/customers', payload);
}

export async function fetchPendingPodVendors(
  payload: PendingPodLookupRequest
): Promise<PendingPodLookupResponse> {
  return http.post('/api/v1/pending-pod-reports/vendors', payload);
}

export type PendingPodRow = {
  cn_number: string;
  consigner: string | null;
  consignee: string | null;
  delivery_lhc_number: string | null;
  delivery_lhc_date: string | null;
  delivery_lhc_branch: string | null;
  vehicle_type: string | null;
  vehicle_number: string | null;
  lhc_origin: string | null;
  lhc_destination: string | null;
  delivery_date: string | null;
  pod_pending_days: number | null;
  pod_submission_deadline: string | null;
  pod_delay_days: number | null;
  vendor_type: string | null;
  driver_number: string | null;
  vendor_name: string | null;
};

export type FetchPendingPodDataRequest = {
  filters: Record<string, string[]>;
  search?: string;
  cust_code?: string;
  vendor_code?: string;
  status?: PendingPodStatus;
  from_date?: string;
  to_date?: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_order: 'asc' | 'desc';
};

export type FetchPendingPodDataResponse = {
  rows: PendingPodRow[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
};

export async function fetchPendingPodData(
  payload: FetchPendingPodDataRequest
): Promise<FetchPendingPodDataResponse> {
  return http.post('/api/v1/pending-pod-reports/fetch-data', payload);
}

export type DownloadPendingPodsRequest = {
  filters: Record<string, string[]>;
  search?: string;
  cust_code?: string;
  vendor_code?: string;
  status?: PendingPodStatus;
  from_date?: string;
  to_date?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
};

export async function downloadPendingPods(payload: DownloadPendingPodsRequest): Promise<Blob> {
  return http.post('/api/v1/pending-pod-reports/download-pending-pods', payload, { responseType: 'blob' });
}

export interface PendingPodNote {
  cn_number: string;
  content: string;
  photo_url?: string;
  created_by_ecode: string;
  created_by_name: string;
  created_at: string;
}

export interface PendingPodNoteListResponse {
  cn_number: string;
  notes: PendingPodNote[];
}

export interface PendingPodNoteCreatePayload extends Record<string, unknown> {
  content: string;
}

export async function getPendingPodNotes(cnNumber: string): Promise<PendingPodNoteListResponse> {
    return http.get<PendingPodNoteListResponse>(`/api/v1/pending-pod-reports/notes/${encodeURIComponent(cnNumber)}`);
}

export async function createPendingPodNote(cnNumber: string, payload: PendingPodNoteCreatePayload): Promise<PendingPodNote> {
    return http.post<PendingPodNote>(`/api/v1/pending-pod-reports/notes/${encodeURIComponent(cnNumber)}/add`, payload);
}